import type { CorSelo } from '../../componentes/base';

/** Fase 2 (W8): compra direta no web (tipos da API e a prévia das parcelas, com a mesma regra de centavos do servidor). */

export const FORMAS_PAGAMENTO = ['pix', 'boleto', 'transferencia', 'cartao_credito', 'cartao_debito', 'dinheiro', 'cheque', 'outro'] as const;
export type FormaPagamento = (typeof FORMAS_PAGAMENTO)[number];
export type EstadoCompra = 'ativa' | 'cancelada';
export const ESTADOS_COMPRA: EstadoCompra[] = ['ativa', 'cancelada'];
export const COR_COMPRA: Record<EstadoCompra, CorSelo> = { ativa: 'verde', cancelada: 'perigo' };
export const COR_CONTA: Record<string, CorSelo> = { em_aberto: 'azul', parcial: 'laranja', pago: 'verde', cancelado: 'perigo' };
/** Lote 09/10 (C3): "Serviço (NFS-e)" ou "Produto (NF-e de entrada)", como no sistema antigo. */
export const TIPOS_COMPRA = ['servico', 'produto'] as const;
export type TipoCompra = (typeof TIPOS_COMPRA)[number];

export interface Compra {
  id: string;
  numero: string;
  /** Lote 09/10 (C3). */
  tipo?: TipoCompra;
  conta_prevista_id?: string | null;
  conta_prevista_nome?: string | null;
  fornecedor_id: string;
  fornecedor_nome: string;
  descricao: string;
  nf_numero: string | null;
  nf_serie: string | null;
  nf_chave: string | null;
  nf_emissao: string | null;
  competencia: string;
  condicao_pagamento: string;
  parcelas: number;
  primeiro_vencimento: string;
  forma_pagamento: FormaPagamento | null;
  /** Fase 2 (W8 + W6): forma do catálogo do Cadastros (nome livre). */
  forma_pagamento_nome?: string | null;
  categoria_id: string | null;
  categoria_nome: string | null;
  centro_custo_id: string | null;
  centro_custo_nome: string | null;
  total_centavos: number;
  moeda: string;
  estado: EstadoCompra;
  itens: number;
  observacoes: string | null;
  motivo_cancelamento: string | null;
  cancelado_em: string | null;
  /** Fase 2 (W19, K5): campos personalizados da compra. */
  extras?: Record<string, unknown>;
  created_at: string;
}

export interface CompraDetalhe extends Compra {
  itens_detalhe: {
    id: string;
    descricao: string;
    quantidade: number;
    valor_unitario_centavos: number;
    total_centavos: number;
    categoria_nome: string | null;
    centro_custo_nome: string | null;
    /** Lote 09/10 (C3): entrada no Estoque do item de produto. */
    movimentacao_id?: string | null;
  }[];
  contas: { id: string; numero: string | null; parcela: number; parcelas: number; vencimento: string; valor_centavos: number; estado: string | null; saldo_centavos: number | null }[];
}

/** Valor de cada parcela: as N-1 primeiras para baixo e a última com o centavo (o mesmo que o servidor grava). */
export function valoresDasParcelas(total: number, n: number): number[] {
  if (!Number.isSafeInteger(total) || total <= 0 || !Number.isInteger(n) || n < 1 || total < n) return [];
  const base = Math.floor(total / n);
  return Array.from({ length: n }, (_, k) => (k === n - 1 ? total - base * (n - 1) : base));
}

/** Quantas parcelas a condição gera: mensal pelo número; prazos "30/60/90" por quantidade de prazos; à vista, uma. */
export function parcelasDaCondicao(condicao: 'avista' | 'mensal' | 'prazos', parcelas: string, prazos: string): number {
  if (condicao === 'mensal') {
    const n = Number(parcelas);
    return Number.isInteger(n) && n >= 1 && n <= 120 ? n : 0;
  }
  if (condicao === 'prazos') {
    const t = prazos.trim();
    return /^\d{1,3}(\/\d{1,3})*$/.test(t) ? t.split('/').length : 0;
  }
  return 1;
}

/** Total de uma linha (quantidade x valor unitário), arredondado para o par como o servidor. */
export function totalDaLinha(quantidade: number, unitario: number): number {
  const v = quantidade * unitario;
  const piso = Math.floor(v);
  const fracao = v - piso;
  if (Math.abs(fracao - 0.5) < 1e-9) return piso % 2 === 0 ? piso : piso + 1;
  return Math.round(v);
}

/** Lote 09/10 (B2): o que o serviço contratado sugere no item, só no que está vazio (valor, descrição e categoria). */
export function sugestaoDoContratado<L extends { descricao: string; valor: number | null; categoria: string }>(
  l: L,
  sc: { nome: string; valor_referencia_centavos: number | null; categoria_despesa_id: string | null },
  categoriasValidas: string[],
): L {
  return {
    ...l,
    descricao: l.descricao.trim() ? l.descricao : sc.nome,
    valor: l.valor ?? sc.valor_referencia_centavos ?? null,
    categoria: l.categoria || (sc.categoria_despesa_id && categoriasValidas.includes(sc.categoria_despesa_id) ? sc.categoria_despesa_id : ''),
  };
}

/** Soma meses sem estourar o fim do mês (31/01 + 1 mês = 28 ou 29/02), a mesma regra do servidor. */
export function somarMeses(data: string, meses: number): string {
  const [a, m, d] = data.split('-').map(Number) as [number, number, number];
  const alvo = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(d, ultimo));
  return alvo.toISOString().slice(0, 10);
}

/**
 * Lote 09/10 (C3): vencimento de cada parcela para a prévia em tabela (Parcela, Vencimento, Valor), como no antigo e com a
 * regra do servidor (`vencimentosDaCompra`): mensal soma meses ao primeiro vencimento; prazos "30/60/90" somam a diferença
 * de dias ao primeiro; à vista, só o primeiro. Condição ou data inválida devolve lista vazia.
 */
export function vencimentosDaPrevia(condicao: 'avista' | 'mensal' | 'prazos', parcelas: string, prazos: string, primeiro: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(primeiro)) return [];
  const n = parcelasDaCondicao(condicao, parcelas, prazos);
  if (!n) return [];
  if (condicao === 'mensal') return Array.from({ length: n }, (_, k) => somarMeses(primeiro, k));
  if (condicao === 'avista') return [primeiro];
  const dias = prazos.trim().split('/').map(Number);
  const base = dias[0]!;
  return dias.map((d) => {
    const x = new Date(`${primeiro}T00:00:00Z`);
    x.setUTCDate(x.getUTCDate() + d - base);
    return x.toISOString().slice(0, 10);
  });
}

/** Lote 09/10 (C3): ao trocar o tipo, o item perde o catálogo do outro tipo (serviço contratado ou produto). */
export function linhaDoTipo<L extends { contratado: string; produto: string; produtoRotulo: string }>(l: L, tipo: TipoCompra): L {
  return tipo === 'produto' ? { ...l, contratado: '' } : { ...l, produto: '', produtoRotulo: '' };
}
